import type { ComponentType } from 'react';
import type { tLiveAlert } from 'src/alerts/AlertCenter.types';
import {
  META_PRICING_ALERT_ID,
  META_PRICING_BALANCE_ALERT_ID,
  META_PRICING_TIPS_ALERT_ID,
} from 'src/alerts/MetaPricingNotice.types';
import AccountHealthDetail from './AccountHealthDetail.component';
import LeadAgentAcceptanceDetail from './LeadAgentAcceptanceDetail.component';
import LeadAgentExpiredDetail from './LeadAgentExpiredDetail.component';
import LeadAgentTrialCapDetail from './LeadAgentTrialCapDetail.component';
import LeadAgentUsageDetail from './LeadAgentUsageDetail.component';
import MetaPricingDetail from './MetaPricingDetail.component';
import PastDueFinalDetail from './PastDueFinalDetail.component';

export type tAlertDetailRendererProps = {
  alert: tLiveAlert;
  /** Close the detail modal (a renderer action that reads as dismissal). */
  onRequestClose: () => void;
  /**
   * Report that a modal NESTED inside this renderer opened/closed (the
   * trial-cap unlock/accept flows). While one is open the host detail must
   * refuse to dismiss itself — Escape included — or its unmount takes the
   * nested modal down mid-interaction (the REL-2 mid-charge stranding).
   */
  onNestedModalChange: (isOpen: boolean) => void;
};

/**
 * U4/RET-1878 — alert id → rich detail renderer. A renderer owns the modal's
 * WHOLE body region (most compose `AlertDetailBody` above their extras);
 * alerts with no entry fall back to title, body and actions. One renderer per
 * rich alert, adopted from the legacy banner bodies (U5 de Roby): the
 * capacity gauge, the account-health video and policy links, the trial-cap
 * option list.
 */
export const ALERT_DETAIL_RENDERERS: Record<
  string,
  ComponentType<tAlertDetailRendererProps>
> = {
  'la-usage-capped': LeadAgentUsageDetail,
  'la-usage-near': LeadAgentUsageDetail,
  'account-health': AccountHealthDetail,
  'la-trial-cap': LeadAgentTrialCapDetail,
  // RET-1956 — the two families whose CTA used to navigate away from the money.
  'la-expired': LeadAgentExpiredDetail,
  'la-acceptance': LeadAgentAcceptanceDetail,
  // RET-1269 U14 — the day 9+ past-due rung. Same reason as the two above:
  // `detail` is the only CTA kind that can carry a charge. Only the FINAL rung
  // has one; the quiet rungs pay through an `external` CTA straight to the
  // hosted invoice, which needs no modal in between.
  'past-due-final': PastDueFinalDetail,
  /*
   * AGI-428. Its CTA is `detail` for the footer-contract reason below, not
   * because it carries a charge: the renderer paints the "No volver a mostrar"
   * checkbox, "Cerrar" and the external CTA as one row, so the modal must not
   * add a button of its own. It is also the only renderer that is opened
   * automatically (see `AutoOpenNoticeHost`), which is why it writes the
   * "seen this session" mark itself instead of trusting the opener.
   */
  [META_PRICING_ALERT_ID]: MetaPricingDetail,
  // AGI-428, messages 2 (25-sep) and 3 (6-oct): the same renderer and the
  // same dialog; the renderer tells the messages apart by the notice key.
  [META_PRICING_TIPS_ALERT_ID]: MetaPricingDetail,
  [META_PRICING_BALANCE_ALERT_ID]: MetaPricingDetail,
};

/**
 * AGI-428. The families whose detail header is the title and the close button
 * only: no tier icon badge, a larger title and a subtle rule under it.
 *
 * On purpose, not an oversight. Ariel's staging review (16-sep) of the
 * Meta-pricing notice: the icon in front of the title pushed the dialog's
 * visual and metric symmetry to the right and left the title misaligned with
 * the body under it, and the title was too small to lead the body. The review
 * was of THIS dialog, so the change is scoped to it: every other family keeps
 * its badge header pixel for pixel until it gets the same look.
 *
 * A set in this registry rather than a prop from the renderer, so the header
 * stays the modal's decision: renderers own the body region and nothing else.
 */
export const ALERT_DETAIL_PLAIN_HEADER_IDS: ReadonlySet<string> = new Set([
  META_PRICING_ALERT_ID,
  META_PRICING_TIPS_ALERT_ID,
  META_PRICING_BALANCE_ALERT_ID,
]);

/**
 * THE FOOTER CONTRACT, and the one invariant that keeps it honest:
 *
 *   a renderer paints its own action row  ⟺  its declaration's CTA is `detail`
 *
 * Both directions matter, and each was broken once:
 *
 *  - left to right. The trial-cap detail painted three unlock options while
 *    its CTA was `navigate`, so the footer added a fourth button carrying the
 *    SAME "Desbloquear ahora" label, plus an acknowledgment — five buttons in
 *    a 448px dialog (reported 2026-08-28).
 *  - right to left. Declaring `detail` on a variant the renderer paints
 *    nothing for opens an empty dialog. That is why the two last-days
 *    countdown variants keep a `navigate` CTA: R13 gives them no self-serve
 *    unlock, so there is nothing for a detail to offer.
 *
 * The modal reads `kind === 'detail'` and needs no id list. A renderer that
 * only EXPLAINS — the usage gauge, the account-health video — declares some
 * other kind and keeps the footer, which is its only button.
 */
